import {useState} from "react";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {z} from "zod";
import {createRestaurant} from "../../api/api";
import {useToast} from "../../hooks/use-toast";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "../ui/form";
import {Input} from "../ui/input";
import {Button} from "../ui/button";
import {Textarea} from "../ui/textarea";
import {Alert, AlertDescription, AlertTitle} from "../ui/alert";
import {AlertCircle} from "lucide-react";

const restaurantFormSchema = z.object({
    name: z.string().min(1, {message: "Restaurant name is required"}),
    description: z.string().min(10, {message: "Description must be at least 10 characters"}),
    image: z.string().url({message: "Please provide a valid image URL"}),
    cuisine: z.string().min(1, {message: "Cuisine is required"}),
    location: z.string().min(1, {message: "Location is required"}),
    distance: z.coerce.number().positive({message: "Distance must be a positive number"}),
    rating: z.coerce.number().min(1, {message: "Rating must be at least 1"}).max(5, {message: "Rating cannot exceed 5"}),
    price: z.coerce.number().positive({message: "Price must be a positive number"}),
});

type RestaurantFormValues = z.infer<typeof restaurantFormSchema>;

interface AddRestaurantFormProps {
    userId: number;
    onSuccess?: () => void;
}

const AddRestaurantForm = ({userId, onSuccess}: AddRestaurantFormProps) => {
    const {toast} = useToast();
    const [error, setError] = useState<string | null>(null);

    const form = useForm<RestaurantFormValues>({
        resolver: zodResolver(restaurantFormSchema),
        defaultValues: {
            name: "",
            description: "",
            image: "",
            cuisine: "",
            location: "",
            distance: 1.0,
            rating: 4.5,
            price: 15.0,
        },
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    const onSubmit = async (data: RestaurantFormValues) => {
        setError(null);
        setIsSubmitting(true);

        try {
            await createRestaurant({...data, owner_id: userId});

            toast({
                title: "Restaurant added",
                description: "Your restaurant has been added successfully",
            });

            // Refetch manually if needed:
            // await queryClient.invalidateQueries({ queryKey: ["/api/restaurants/owner"] });

            if (onSuccess) onSuccess();
            form.reset();
        } catch (error) {
            setError(error instanceof Error ? error.message : "Failed to add restaurant. Please try again.");

            toast({
                title: "Error adding restaurant",
                description: "There was a problem adding your restaurant",
                variant: "destructive",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const cuisines = [
        "Italian",
        "French",
        "Japanese",
        "Mediterranean",
        "Indian",
        "Steakhouse",
        "Chinese",
        "Mexican",
        "Thai",
        "Other",
    ];

    return (
        <div className="space-y-6 py-4">
            <div>
                <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
                    Add New Restaurant
                </h2>
                <p className="text-gray-600 dark:text-gray-300">
                    Fill in the details to add your restaurant to SurpriseTable
                </p>
            </div>

            {error && (
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4"/>
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <FormField
                        control={form.control}
                        name="name"
                        render={({field}) => (
                            <FormItem>
                                <FormLabel>Restaurant Name</FormLabel>
                                <FormControl>
                                    <Input
                                        placeholder="Enter restaurant name"
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage/>
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="description"
                        render={({field}) => (
                            <FormItem>
                                <FormLabel>Description</FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder="Describe your restaurant and the types of meals you serve"
                                        rows={4}
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage/>
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="image"
                        render={({field}) => (
                            <FormItem>
                                <FormLabel>Restaurant Image URL</FormLabel>
                                <FormControl>
                                    <Input
                                        placeholder="Enter image URL"
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage/>
                            </FormItem>
                        )}
                    />

                    <div className="grid gap-4 sm:grid-cols-2">
                        <FormField
                            control={form.control}
                            name="cuisine"
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>Cuisine Type</FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            list="cuisines"
                                            placeholder="Select or enter cuisine type"
                                        />
                                    </FormControl>
                                    <datalist id="cuisines">
                                        {cuisines.map((cuisine) => (
                                            <option key={cuisine} value={cuisine}/>
                                        ))}
                                    </datalist>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="location"
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>Location</FormLabel>
                                    <FormControl>
                                        <Input
                                            placeholder="Enter location"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="distance"
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>Distance (km)</FormLabel>
                                    <FormControl>
                                        <Input
                                            type="number"
                                            step="0.1"
                                            min="0"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="rating"
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>Rating (1-5)</FormLabel>
                                    <FormControl>
                                        <Input
                                            type="number"
                                            step="0.1"
                                            min="1"
                                            max="5"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="price"
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>Price (€)</FormLabel>
                                    <FormControl>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />
                    </div>

                    <div className="flex justify-end space-x-2 pt-4">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                if (onSuccess) onSuccess();
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="bg-primary text-white hover:bg-red-700"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Adding..." : "Add Restaurant"}
                        </Button>
                    </div>
                </form>
            </Form>
        </div>
    );
};

export default AddRestaurantForm;
